/**
 * LOS ENCARGOS (25-sep-2026): cada entregable de las skills de Rais, pedido como la skill lo hace.
 *
 * Sustituyen a los botones de la etapa Publicación que abrían el chat con un texto escrito de memoria
 * («Hazme el brief de portada para un diseñador…»): la skill de portada entrega tres conceptos con
 * prompt de Midjourney, prompt de DALL-E y brief de Canva, y el chat no dio ni uno. Aquí cada fila es
 * un modo real de una skill; el motor lo hace en segundo plano y deja el Word en la carpeta del libro.
 * Antes de pulsar se ve qué se va a leer, qué falta y cuánto cuesta.
 */
import { useEffect, useState } from "react";
import { catalogoModelos, dolares, encargos as pedirCatalogo, modelosAsignados, mostrarEnCarpeta, pedirEncargo } from "../api";
import type { Encargo, ModeloCatalogo, Trabajo } from "../api";
import { costeEncargo, nombreDeRuta } from "../logica";
import { ListaDePasos } from "./ListaDePasos";

/** Cómo se llama en pantalla cada documento del contexto. */
const DOC: Record<string, string> = {
  sinopsis: "sinopsis", biblia: "biblia", escaleta: "escaleta", estilo: "guía de estilo", perfil: "perfil de autor",
  promesas: "promesas", prohibidas: "palabras prohibidas", canon: "canon de nombres", lectura: "informe de lectura",
  editor: "informe del editor", beta: "lectores beta", mercado: "informe de mercado",
};

export default function Encargos({ carpeta, doc, ids, etapa, trabajos, pulso, alLanzar, titulo, sub }: {
  carpeta: string; doc: string | null; ids?: string[]; etapa?: string; trabajos: Trabajo[]; pulso?: number;
  alLanzar: () => void; titulo: string; sub?: string;
}) {
  const [lista, setLista] = useState<Encargo[] | null | undefined>(undefined);
  const [precios, setPrecios] = useState<Record<string, ModeloCatalogo | undefined>>({});
  const [abierto, setAbierto] = useState<string | null>(null);
  const [datos, setDatos] = useState<Record<string, string>>({});
  const [pidiendo, setPidiendo] = useState<string | null>(null);
  const [fallo, setFallo] = useState<{ id: string; texto: string } | null>(null);
  const vivos = trabajos.filter((t) => t.tipo.startsWith("encargo-") && t.estado === "en_curso").length;

  useEffect(() => {
    if (!carpeta) return;
    pedirCatalogo(carpeta, doc).then(setLista).catch(() => setLista(undefined));
  }, [carpeta, doc, pulso, vivos]);
  useEffect(() => {
    Promise.all([modelosAsignados(), catalogoModelos()]).then(([m, c]) => {
      const de = (ag: string) => c.modelos.find((x) => x.id === (m.porAgente ?? {})[ag]);
      setPrecios({ publicar: de("publicar"), coach: de("coach"), author: de("author") });
    }).catch(() => {});
  }, []);

  if (lista === null) {
    return (
      <section className="carta">
        <h2>{titulo}</h2>
        <div className="aviso">Tu motor aún no tiene los encargos: instala la última versión del Studio (en Ajustes → Descargas).</div>
      </section>
    );
  }
  const filas = (lista ?? []).filter((e) => (ids ? ids.includes(e.id) : e.etapa === etapa));
  if (lista !== undefined && !filas.length) return null;

  async function lanzar(e: Encargo) {
    const falta = e.pide.find((p) => p.obligatorio && !(datos[`${e.id}.${p.campo}`] ?? "").trim());
    if (e.pide.length && abierto !== e.id) {
      // Lo que el motor propone (el título y el autor, 26-sep) sale ya escrito: el autor lo confirma o lo cambia.
      const propuestos = Object.fromEntries(e.pide.filter((p) => p.valor && !datos[`${e.id}.${p.campo}`]).map((p) => [`${e.id}.${p.campo}`, p.valor!]));
      setDatos({ ...datos, ...propuestos });
      setAbierto(e.id); return;
    }
    if (falta) { setFallo({ id: e.id, texto: `Falta: ${falta.etiqueta.replace(/[¿?]/g, "")}.` }); return; }
    setPidiendo(e.id); setFallo(null);
    try {
      const d = Object.fromEntries(e.pide.map((p) => [p.campo, datos[`${e.id}.${p.campo}`] ?? ""]));
      await pedirEncargo(e.id, carpeta, doc, d, !!e.hecho);
      setAbierto(null); alLanzar();
    } catch (err) { setFallo({ id: e.id, texto: (err as Error).message }); }
    finally { setPidiendo(null); }
  }

  return (
    <section className="carta">
      <h2>{titulo}{sub && <span className="sub">{sub}</span>}</h2>
      {lista === undefined && <p className="vacio">…</p>}
      <div className="lista-etapa">
        {filas.map((e) => {
          const vivo = trabajos.find((t) => t.tipo === "encargo-" + e.id) ?? null;
          const enMarcha = vivo?.estado === "en_curso" || pidiendo === e.id;
          const coste = costeEncargo(e.caracteres, precios[e.agente]);
          const ruta = e.hecho?.ruta ?? (vivo?.estado === "hecho" ? String(vivo.resultado?.ruta ?? "") : "");
          return (
            <div className="encargo" key={e.id}>
              <div className="fila-etapa">
                <div className="que">
                  <b>{e.titulo}</b>
                  <span className="vacio">{e.descripcion}</span>
                  <span className="vacio encargo-lee">
                    {enMarcha ? "en marcha, en segundo plano…"
                      : vivo?.estado === "fallido" ? `no se pudo terminar: ${vivo.error}`
                      : <>
                          {e.tiene.length ? `Lee tu ${e.tiene.map((k) => DOC[k] ?? k).join(", ")}` : "Trabaja con el manuscrito"}
                          {e.faltan.length ? ` · no hay ${e.faltan.map((k) => DOC[k] ?? k).join(", ")}` : ""}
                          {coste != null ? ` · ~${dolares(coste)} de tu llave` : ""}
                        </>}
                  </span>
                </div>
                {e.hecho && !enMarcha && <span className="chip-estado ok">hecho</span>}
                {enMarcha
                  ? <span className="chip-estado">en marcha</span>
                  : <button className={e.hecho ? "btn texto" : "btn fantasma"} disabled={!carpeta || (!doc && e.id !== "perfil-autor" && e.id !== "auditoria-kdp")}
                            onClick={() => lanzar(e)}>{abierto === e.id ? "Encargarlo" : e.hecho ? "Rehacer" : "Pedirlo"}</button>}
              </div>
              {abierto === e.id && !enMarcha && (
                <div className="formulario encargo-datos">
                  {e.pide.map((p) => (
                    <label className="campo" key={p.campo}>
                      <span>{p.etiqueta} <i>{p.ayuda}</i></span>
                      {p.largo
                        ? <textarea className="escribir" rows={6} value={datos[`${e.id}.${p.campo}`] ?? ""}
                                    onChange={(ev) => setDatos({ ...datos, [`${e.id}.${p.campo}`]: ev.target.value })} />
                        : <input className="escribir" value={datos[`${e.id}.${p.campo}`] ?? ""}
                                 onChange={(ev) => setDatos({ ...datos, [`${e.id}.${p.campo}`]: ev.target.value })} />}
                    </label>
                  ))}
                  <div className="botones"><button className="btn texto" onClick={() => setAbierto(null)}>Cancelar</button></div>
                </div>
              )}
              {vivo?.estado === "en_curso" && <ListaDePasos trabajo={vivo} />}
              {e.limite && <p className="vacio encargo-limite">{e.limite}</p>}
              {fallo?.id === e.id && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo.texto}</p>}
              {ruta && !enMarcha && (
                <p className="vacio encargo-hecho">
                  En tu carpeta: <span className="mono">{nombreDeRuta(ruta)}</span>
                  {e.hecho?.fecha && ` · ${new Date(e.hecho.fecha).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}`}{" "}
                  <button className="btn texto" onClick={() => mostrarEnCarpeta(ruta)}>Enseñar en la carpeta</button>
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
